import { FC, FormEvent, ReactNode, useState } from 'react';
import type { NovoApontamento, OpcaoApontamento } from '@/types/dashboards';
import {
  errosDaApi,
  rotuloSku,
  skuDoTexto,
  type CatalogoApontamento,
  type ErrosApontamento,
  type SkuOpcao,
} from '@/utils/equiplexApontamentos';

interface Props {
  opcoes: { motivos: OpcaoApontamento[]; turnos: OpcaoApontamento[] };
  /** `null` = sem retrato da Visão geral: máquina e SKU viram texto livre. */
  catalogo: CatalogoApontamento | null;
  inicial: NovoApontamento;
  rotuloSalvar: string;
  onSalvar: (dados: NovoApontamento) => Promise<void>;
  onCancelar?: () => void;
}

const CAMPO =
  'w-full rounded-lg border border-theme-border bg-surface-primary px-3 py-2 text-sm text-content-primary focus:border-accent focus:outline-none';
const SEM_ERROS: ErrosApontamento = { geral: '', campos: {} };

const Campo: FC<{ id: string; rotulo: string; erro?: string; dica?: string; className?: string; children: ReactNode }> = ({
  id,
  rotulo,
  erro,
  dica,
  className = '',
  children,
}) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label htmlFor={id} className="text-xs font-medium text-content-secondary">
      {rotulo}
    </label>
    {children}
    {erro ? (
      <p id={`${id}-erro`} className="text-xs text-theme-danger">
        {erro}
      </p>
    ) : (
      dica && <p className="text-xs text-content-muted">{dica}</p>
    )}
  </div>
);

const CampoMaquina: FC<{ valor: string; catalogo: CatalogoApontamento | null; erro?: string; onChange: (v: string) => void }> = ({
  valor,
  catalogo,
  erro,
  onChange,
}) => {
  if (!catalogo) {
    return (
      <Campo id="ap-maquina" rotulo="Máquina" erro={erro} dica="Em branco = a fábrica toda.">
        <input id="ap-maquina" className={CAMPO} maxLength={60} value={valor} onChange={(e) => onChange(e.target.value)} />
      </Campo>
    );
  }
  // Apontamento antigo pode citar máquina que saiu da janela do painel: continua na lista.
  const conhecida = !valor || catalogo.grupos.some((g) => g.maquinas.includes(valor));
  return (
    <Campo id="ap-maquina" rotulo="Máquina" erro={erro}>
      <select id="ap-maquina" className={CAMPO} value={valor} onChange={(e) => onChange(e.target.value)}>
        <option value="">Fábrica toda (sem máquina específica)</option>
        {!conhecida && <option value={valor}>{valor}</option>}
        {catalogo.grupos.map((g) => (
          <optgroup key={g.rotulo} label={g.rotulo}>
            {g.maquinas.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </Campo>
  );
};

const CampoSku: FC<{ texto: string; catalogo: CatalogoApontamento | null; erro?: string; onChange: (v: string) => void }> = ({
  texto,
  catalogo,
  erro,
  onChange,
}) => (
  <Campo id="ap-sku" rotulo="SKU (opcional)" erro={erro} dica="Digite o código ou parte da descrição e escolha na lista.">
    <input
      id="ap-sku"
      className={CAMPO}
      list={catalogo ? 'ap-sku-lista' : undefined}
      value={texto}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="off"
    />
    {catalogo && (
      <datalist id="ap-sku-lista">
        {catalogo.skus.map((s) => (
          <option key={s.codigo} value={rotuloSku(s)} />
        ))}
      </datalist>
    )}
  </Campo>
);

function textoInicialSku(f: NovoApontamento): string {
  if (!f.sku_codigo) return '';
  return f.sku_descricao ? rotuloSku({ codigo: f.sku_codigo, descricao: f.sku_descricao }) : f.sku_codigo;
}

/**
 * O SKU digitado. Com catálogo, só vale o que está na lista — ou o que o apontamento já
 * tinha (SKU que saiu da janela do painel não invalida a edição). Sem catálogo, texto livre.
 */
function skuDoForm(texto: string, inicial: NovoApontamento, catalogo: CatalogoApontamento | null): SkuOpcao | null | 'invalido' {
  const limpo = texto.trim();
  if (inicial.sku_codigo && limpo === textoInicialSku(inicial)) {
    return { codigo: inicial.sku_codigo, descricao: inicial.sku_descricao };
  }
  if (catalogo) return skuDoTexto(limpo, catalogo.skus);
  return limpo ? { codigo: limpo.slice(0, 20), descricao: '' } : null;
}

/** Validação local, igual à do backend: erro de digitação não precisa ir e voltar. */
function validar(form: NovoApontamento, sku: SkuOpcao | null | 'invalido'): NovoApontamento | ErrosApontamento {
  const campos: ErrosApontamento['campos'] = {};
  if (!form.data) campos.data = 'Informe a data.';
  if (!form.motivo) campos.motivo = 'Escolha o motivo.';
  if (form.motivo === 'outro' && !form.observacao.trim()) campos.observacao = 'Motivo "Outro" precisa de observação.';
  if (sku === 'invalido') campos.sku_codigo = 'SKU fora da lista: escolha uma sugestão ou deixe em branco.';
  if (sku === 'invalido' || Object.keys(campos).length > 0) return { geral: 'Confira os campos destacados.', campos };
  return { ...form, sku_codigo: sku?.codigo ?? '', sku_descricao: sku?.descricao ?? '' };
}

/** Formulário de um apontamento (novo ou edição). Não muda número nenhum dos painéis. */
const ApontamentoForm: FC<Props> = ({ opcoes, catalogo, inicial, rotuloSalvar, onSalvar, onCancelar }) => {
  const [form, setForm] = useState<NovoApontamento>(inicial);
  const [skuTexto, setSkuTexto] = useState(() => textoInicialSku(inicial));
  const [erros, setErros] = useState<ErrosApontamento>(SEM_ERROS);
  const [salvando, setSalvando] = useState(false);

  const mudar = <K extends keyof NovoApontamento>(campo: K, valor: NovoApontamento[K]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const pronto = validar(form, skuDoForm(skuTexto, inicial, catalogo));
    if ('geral' in pronto) {
      setErros(pronto);
      return;
    }
    setSalvando(true);
    try {
      await onSalvar(pronto);
      setErros(SEM_ERROS);
    } catch (erro) {
      setErros(errosDaApi(erro, 'Não foi possível salvar o apontamento.'));
    } finally {
      setSalvando(false);
    }
  };

  const { campos } = erros;
  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4" data-testid="apontamento-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Campo id="ap-data" rotulo="Data" erro={campos.data}>
          <input id="ap-data" type="date" className={CAMPO} value={form.data} onChange={(e) => mudar('data', e.target.value)} />
        </Campo>
        <CampoMaquina valor={form.maquina} catalogo={catalogo} erro={campos.maquina} onChange={(v) => mudar('maquina', v)} />
        <Campo id="ap-turno" rotulo="Turno (opcional)" erro={campos.turno}>
          <select id="ap-turno" className={CAMPO} value={form.turno} onChange={(e) => mudar('turno', e.target.value)}>
            {opcoes.turnos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <Campo id="ap-motivo" rotulo="Motivo" erro={campos.motivo}>
          <select id="ap-motivo" className={CAMPO} value={form.motivo} onChange={(e) => mudar('motivo', e.target.value)}>
            <option value="">Escolha…</option>
            {opcoes.motivos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <CampoSku texto={skuTexto} catalogo={catalogo} erro={campos.sku_codigo} onChange={setSkuTexto} />
        <Campo id="ap-quantidade" rotulo="Quantidade afetada (opcional)" erro={campos.quantidade} dica="Em unidades.">
          <input
            id="ap-quantidade"
            type="number"
            min={0}
            step={1}
            className={CAMPO}
            value={form.quantidade ?? ''}
            onChange={(e) => mudar('quantidade', e.target.value === '' ? null : Number(e.target.value))}
          />
        </Campo>
        <Campo id="ap-observacao" rotulo="Observação" erro={campos.observacao} className="sm:col-span-2">
          <textarea
            id="ap-observacao"
            rows={2}
            maxLength={2000}
            className={CAMPO}
            value={form.observacao}
            onChange={(e) => mudar('observacao', e.target.value)}
          />
        </Campo>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3">
        {erros.geral && (
          <p role="alert" className="mr-auto text-sm text-theme-danger">
            {erros.geral}
          </p>
        )}
        {onCancelar && (
          <button type="button" onClick={onCancelar} className="rounded-lg px-4 py-2 text-sm text-content-secondary hover:bg-surface-hover">
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={salvando}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
        >
          {salvando ? 'Salvando…' : rotuloSalvar}
        </button>
      </div>
    </form>
  );
};

export default ApontamentoForm;
